import { datadogLogs, HandlerType } from '@datadog/browser-logs';
export { StatusType as LOGGER_LEVEL } from '@datadog/browser-logs';

// src/Logger.ts

// src/constants.ts
var SENSITIVE_KEYS = [
  "password",
  "Authorization",
  "confirmationPassword",
  "newPassword",
  "currentPassword",
  "idToken",
  "token"
];
var DEFAULT_SENSITIVE_TEXT = "<sensitive>";

// src/utils.ts
var isArray = (value) => Array.isArray(value);
var isObject = (value) => value !== null && typeof value === "object";
var mapObject = (object, iteratee) => {
  const source = {};
  for (const key in object) {
    if (Object.hasOwnProperty.call(object, key)) {
      const element = object[key];
      source[key] = iteratee(element, key, object);
    }
  }
  return source;
};
var map = (value, iteratee) => {
  if (isArray(value)) {
    return value.map((element, index) => iteratee(element, index, value));
  }
  if (isObject(value)) {
    return mapObject(value, (value2, key) => iteratee(value2, key, value2));
  }
};
var deepMap = (object, iteratee, maxDepth = 10) => {
  let count = 0;
  const closure = (object2, iteratee2) => {
    if (count > maxDepth) {
      count = 0;
      return null;
    }
    return map(object2, (value, key) => {
      if (isArray(value) || isObject(value)) {
        count++;
        return closure(value, iteratee2);
      }
      count = 0;
      return iteratee2(value, key, object2);
    });
  };
  return closure(object, iteratee);
};
var containsAValue = (value, keys) => {
  if (!value) {
    return false;
  }
  return keys.some((key) => value.includes(key));
};
function isString(value) {
  return typeof value === "string";
}

// src/Logger.ts
var LOGGER_ENVIRONMENT = /* @__PURE__ */ ((LOGGER_ENVIRONMENT2) => {
  LOGGER_ENVIRONMENT2["DEVELOPMENT"] = "development";
  LOGGER_ENVIRONMENT2["TEST"] = "test";
  LOGGER_ENVIRONMENT2["STAGING"] = "staging";
  LOGGER_ENVIRONMENT2["BETA"] = "beta";
  LOGGER_ENVIRONMENT2["PRODUCTION"] = "production";
  LOGGER_ENVIRONMENT2["QA"] = "qa";
  return LOGGER_ENVIRONMENT2;
})(LOGGER_ENVIRONMENT || {});
var Logger = class {
  constructor(options) {
    this.options = options;
  }
  level;
  initialized = false;
  queue = [];
  logSchemaValidator;
  compiledLogSchema;
  // Init SDK with user informations, token and level
  init(initOptions) {
    const { token, debug, level } = this.options;
    datadogLogs.init({
      clientToken: token,
      datacenter: "us",
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["error", "warn"],
      sampleRate: 100,
      beforeSend: initOptions?.beforeSend
    });
    datadogLogs.logger.setHandler(debug ? HandlerType.console : HandlerType.http);
    this.setLevel(level);
    this.initialized = true;
    this.queue.forEach((fn) => fn());
  }
  // set logger context
  setContext(context) {
    datadogLogs.logger.setContext(context);
  }
  // Send logs or enqueue them if the SDK isn't initialized
  logOrEnqueue(fn) {
    this.initialized ? fn() : this.queue.push(fn);
  }
  // Set level of SDK: logs under this level won't be sent
  setLevel(level) {
    this.level = level;
    datadogLogs.logger.setLevel(level);
  }
  // It will scrub the properties which their keys contains some sensitive keywords
  cleanProperties(properties = {}) {
    return deepMap(properties, (value, key) => {
      try {
        const parsedValue = isString(value) && JSON.parse(value);
        if (isObject(parsedValue) || isArray(parsedValue)) {
          return JSON.stringify(this.cleanProperties(parsedValue));
        }
      } catch (e) {
      }
      const keyContainsSensitiveData = isString(key) && containsAValue(key, SENSITIVE_KEYS);
      if (keyContainsSensitiveData) {
        return DEFAULT_SENSITIVE_TEXT;
      }
      return value;
    });
  }
  // Generic method to send logs depending on level parameter
  log(level, message, properties = {}) {
    const cleanedProperties = this.cleanProperties(properties);
    this.logOrEnqueue(() => datadogLogs.logger.log(message, cleanedProperties, level));
  }
  // Send debug logs
  debug(message, properties = {}) {
    const cleanedProperties = this.cleanProperties(properties);
    this.logOrEnqueue(() => datadogLogs.logger.debug(message, cleanedProperties));
  }
  // Send info logs
  info(message, properties = {}) {
    const cleanedProperties = this.cleanProperties(properties);
    this.logOrEnqueue(() => datadogLogs.logger.info(message, cleanedProperties));
  }
  // Send warning logs
  warn(message, properties = {}) {
    const cleanedProperties = this.cleanProperties(properties);
    this.logOrEnqueue(() => datadogLogs.logger.warn(message, cleanedProperties));
  }
  // Send error logs
  error(message, properties = {}) {
    const cleanedProperties = this.cleanProperties(properties);
    this.logOrEnqueue(() => datadogLogs.logger.error(message, cleanedProperties));
  }
};

export { LOGGER_ENVIRONMENT, Logger };
